import { ChapterType, TagType, TourType } from './types';

export function getAllQueryVariables(query: string, prefix = '&') {
  if (!query) return '';
  return `${prefix}${query.split('?').pop()}`;
}

export function getQueryVariable(query: string, variable: string) {
  if (!query || !variable) return '';
  query = decodeURIComponent(query.split('?')[1]);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return '';
}

export function getYoutubeVideoQueryVariable(query: string, variable: string) {
  if (!query || !variable) return '';
  if (getQueryVariable(query, variable)) {
    return getQueryVariable(query, variable);
  }
  return query.split('/').pop();
}

export function getVideoLink(
  url: string,
  settings?: { autoplay?: number | boolean; mute?: boolean; subtitles?: string },
) {
  if (!url) return '';
  let autoplay;
  if (settings && typeof settings.autoplay === 'boolean') {
    autoplay = settings.autoplay ? 1 : 0;
  } else {
    autoplay = settings?.autoplay === undefined ? 1 : settings?.autoplay;
  }

  const mute = settings?.mute === undefined ? false : settings?.mute;
  let muteQueryParam: string = mute ? '&mute=1' : '';

  let urlC: string[] = [];
  if (url.includes('https://vimeo.com/')) {
    urlC = url.split('https://vimeo.com/');
  } else if (url.includes('https://player.vimeo.com/video/')) {
    urlC = url.split('https://player.vimeo.com/video/');
  }
  if (urlC.length > 1) {
    muteQueryParam = mute ? '&&muted=1' : '';
    const subtitlesParam = settings?.subtitles ? `&&texttrack=${settings.subtitles}` : '';
    return `https://player.vimeo.com/video/${urlC[1]}?autoplay=${autoplay}&title=0&byline=0&portrait=0${muteQueryParam}${subtitlesParam}`;
  }
  const subtitlesParam = settings?.subtitles ? `&&hl=${settings.subtitles}` : '';
  return `https://www.youtube.com/embed/${getYoutubeVideoQueryVariable(
    url,
    'v',
  )}?autoplay=${autoplay}${muteQueryParam}${subtitlesParam}`;
}

export async function openTagIframe(this: any, sid: string) {
  const { allTags, tour, tourType } = this.state;

  const foundTag = Array.from(allTags, ([, value]: any) => value)
    // searching by temporary sid from Matterport
    .find((item: any) => item.sid === sid || item.defaultSid === sid);

  if (foundTag) {
    if (!foundTag.type) return null;

    const tag = { ...foundTag };

    if (tag.type === 'video') {
      await this.pauseTourMusic();
    }

    // Allow tag.type with NONE who participate in game actions like chapter, lock
    if (tour?.gameId && tourType === TourType.Game) {
      this.unlockChapter(tag, ChapterType.Tag);
      if (this.handleTagLock(tag)) return null;
    }
    if (tag?.type === TagType.None) {
      return null;
    }
    if (tag?.type === TagType.Navigation) {
      if (this.state.tour?.showNavTools) {
        this.processNavigationPath(tag);
      }
      return null;
    }
    if (tag.embedMedia && [TagType.Popup, TagType.Iframe].includes(tag.type)) {
      return null;
    }
    if (tag.type === TagType.Panorama) {
      this.setState({
        activePano: tag.panoramaId,
        panoramaOn: true,
      });
      return null;
    }
    if (tag.type === TagType.Treedis && tag.url) {
      const segmants = tag.url.split('/');
      const lastSeg = segmants[segmants.length - 1].split('?');
      const id = lastSeg[0];
      if (tour.slug === id && lastSeg.length === 1) return null; // same tour, no query params, we shouldn't get it again

      if (getQueryVariable(tag.url, 'm')) {
        return null;
      }
      const changeMinimap = tour && !tour.customMinimap;
      // isVSReady - the VS should be initialized again
      this.setState({ panoramaOn: false, activePano: null, isVSReady: false }, async () => {
        await this.stopVS();
        this.modelNodes = [];
        this.vsObjects = [];
        this.getTour(id, false, changeMinimap, false, tag.url); // false = dont change the menu/panorama
      });
      return null;
    }
    if (tag.type === TagType.MatterportInstant && tag.url) {
      this.moveModel(
        `/mp-sdk/${process.env.NEXT_PUBLIC_MP_SDK_KEY}/showcase.html?applicationKey=${
          this.state.tour?.user?.mpSDK
        }${getAllQueryVariables(tag.url)}`,
        false,
        true,
      );
      return null;
    }
    if (tag.type === TagType.Matterport && tag.url) {
      this.moveModel(
        `/mp-sdk/${process.env.NEXT_PUBLIC_MP_SDK_KEY}/showcase.html?applicationKey=${
          this.state.tour?.user?.mpSDK
        }${getAllQueryVariables(tag.url)}`,
        false,
        false,
      );
      return null;
    }
    if (tag.type === TagType.Menu) {
      this.setState(
        {
          menuOpen: false,
          activeMenu: null,
        },
        () => {
          this.setState({
            menuOpen: true,
            menuOpened: true,
            activeMenu: {
              id: tag.menuId,
              subMenu: tag.subMenuId,
              subSubMenu: tag.subSubMenuId,
            },
          });
        },
      );
      return null;
    }
    if (tag.type === TagType.Popup) {
      const { noAudioMode } = this.state?.tour;
      tag.url = getVideoLink(tag.url, {
        mute: noAudioMode && this.state.chatToken,
        autoplay: tag.autoPlay,
      });
      this.pauseAllAudio();
    }
    if (tag.type === TagType.Tab && tag.url) {
      window.open(tag.url);
      return null;
    }
    if (tag.type === TagType.Music && tag.url) {
      const { noAudioMode } = this.state?.tour;
      if (noAudioMode && this.state.chatToken) return null;

      const src = `${process.env.NEXT_PUBLIC_CDN}/tours/${this.state.tour.id}/tags/${tag.url}`;
      let audioContainer = this.getAudioContainer('tagAudio');

      if (audioContainer?.tagId !== tag.id) {
        audioContainer = this.createAudioStream('tagAudio', src, true);
        audioContainer.tagId = tag.id;
      }

      const play = async () => {
        try {
          await this.pauseAllAudio('tagAudio');
          const status = await this.playContainerAudio('tagAudio');

          this.setState({ mainPlaying: false, otherPlaying: status });
        } catch (error) {
          if (error instanceof DOMException) {
            // eslint-disable-next-line no-console
            console.error(error.message);
          }
          setTimeout(play, 500);
        }
      };

      play();
      return null;
    }
    if (tag.type === TagType.VideoChat) {
      this.setState({
        videoChatTagPopOn: true,
        openedTag: {
          ...tag,
          sid: tag.defaultSid || tag.sid,
        },
      });
      this.pauseAllAudio();
      return null;
    }
    if (tag.type === TagType.ConferenceChat) {
      this.setState({
        chatOpen: true,
        chatRoom: {
          tag: {
            ...tag,
            sid: tag.defaultSid || tag.sid,
          },
          client: tag?.client,
        },
      });
      return null;
    }

    this.setState({
      tagPopOn: true,
      openedTag: tag,
      closedElementPopup: false,
    });

    return tag;
  }
  return null;
}
